import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { api, downloadFile } from "../api/client.ts"
import type { Case, Shipment } from "../api/types.ts"
import { useApiQuery, useAsyncAction } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { useSession } from "../auth/SessionProvider.tsx"
import { toDateString } from "../lib/date-presets.ts"
import { shipmentSteps } from "../lib/shipment-steps.ts"
import { ShipmentBadge } from "../components/badges.tsx"
import { caseColumns } from "../components/case-columns.tsx"
import { useCaseDetailPanel } from "../components/CaseDetailPanel.tsx"
import { formatWeight, nullable } from "../components/format.ts"
import {
  Button,
  Card,
  CardHeader,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNote,
  Field,
  FormGrid,
  Icon,
  Input,
  PageHeader,
  Skeleton,
  Stepper,
  type Column,
} from "../ui/index.ts"

const today = () => toDateString()

export function ShipmentDetailPage() {
  const { t, locale } = useI18n()
  const { user } = useSession()
  const { id } = useParams()
  const shipmentId = Number(id)

  const detailQuery = useApiQuery<{ shipment: Shipment; cases: Case[] }>(
    `/api/shipments/${shipmentId}`,
  )
  const action = useAsyncAction()
  // 인보이스 내려받기는 상태를 바꾸지 않으므로 다른 버튼을 잠그지 않는다.
  const download = useAsyncAction()

  const [shipForm, setShipForm] = useState({ shippedOn: today(), seinoNo: "", boxCount: "1" })
  const [osakaForm, setOsakaForm] = useState({ osakaArrivedOn: today(), vesselOn: "" })
  const [departForm, setDepartForm] = useState({ panstarTracking: "", vesselOn: "" })
  const [arriveForm, setArriveForm] = useState({ krArrivedOn: today() })

  const shipment = detailQuery.data?.shipment ?? null
  const cases = detailQuery.data?.cases ?? []

  const detail = useCaseDetailPanel({ onChanged: () => void detailQuery.reload(), rows: cases })

  /** 무엇을 하든 끝나면 다시 읽는다 — 상태가 바뀌면 다음에 할 일도 달라진다. */
  async function run(call: () => Promise<unknown>) {
    if (await action.run(call)) await detailQuery.reload()
  }

  /** 인보이스 번호는 처음 내려받을 때 붙는다 — 받은 뒤 다시 읽어야 번호가 보인다. */
  async function downloadInvoice(fileName: string) {
    const ok = await download.run(() =>
      downloadFile(`/api/shipments/${shipmentId}/invoice.xlsx`, fileName),
    )
    if (ok) await detailQuery.reload()
  }

  const error = action.error ?? download.error ?? detailQuery.error
  if (error && !shipment) return <ErrorNote error={error} />
  if (!shipment || !user) return <Skeleton lines={6} />

  // 일본 구간(출고·오사카·출항)은 쿨트랙JP가, 한국 입고는 화주가 찍는다.
  const canShip = user.role === "warehouse" || user.role === "admin"
  const canCloseKr = user.role === "shipper" || user.role === "admin"
  const busy = action.busy

  const cols = caseColumns(t, locale)
  const columns: Column<Case>[] = [
    cols.seq(),
    cols.orderNo({ primary: true }),
    cols.trackingNo({ priority: "md" }),
    cols.customerName(),
    cols.weight(),
    cols.status(),
  ]

  return (
    <>
      <PageHeader
        above={
          <Link to="/shipments" className="back-link">
            <Icon name="chevronLeft" size={14} />
            {t("shipments.title")}
          </Link>
        }
        title={
          <>
            {shipment.shipmentNo}
            <ShipmentBadge status={shipment.status} />
          </>
        }
        actions={
          <Button
            icon="download"
            disabled={busy}
            onClick={() => void downloadInvoice(`${shipment.shipmentNo}.xlsx`)}
          >
            {t("shipments.invoice")}
          </Button>
        }
      />

      <ErrorNote error={error} />

      <Stepper
        ariaLabel={t("shipments.title")}
        steps={shipmentSteps(shipment.status).map((step) => ({
          ...step,
          label: t(`shipments.step.${step.id}`),
        }))}
      />

      <Card>
        <CardHeader title={t("shipments.summary")} icon="box" />
        <DefinitionList
          rows={[
            { label: t("shipments.caseCount"), value: String(shipment.caseCount) },
            { label: t("shipments.totalWeight"), value: formatWeight(shipment.totalWeightG) },
            { label: t("shipments.boxCount"), value: nullable(shipment.boxCount) },
            { label: t("shipments.shippedOn"), value: nullable(shipment.shippedOn) },
            { label: t("shipments.seinoNo"), value: nullable(shipment.seinoNo) },
            { label: t("shipments.osakaArrivedOn"), value: nullable(shipment.osakaArrivedOn) },
            { label: t("shipments.vesselOn"), value: nullable(shipment.vesselOn) },
            { label: t("shipments.panstarTracking"), value: nullable(shipment.panstarTracking) },
            { label: t("shipments.krArrivedOn"), value: nullable(shipment.krArrivedOn) },
            { label: t("case.invoiceNo"), value: nullable(shipment.invoiceNo) },
          ]}
        />
      </Card>

      {canShip && shipment.status === "draft" ? (
        <Card tone="accent">
          <CardHeader title={t("shipments.ship")} icon="ship" />
          <FormGrid columns={3}>
            <Field label={t("shipments.shippedOn")} htmlFor="ship-on" required>
              <Input
                id="ship-on"
                type="date"
                value={shipForm.shippedOn}
                onChange={(event) => setShipForm({ ...shipForm, shippedOn: event.target.value })}
              />
            </Field>
            <Field label={t("shipments.seinoNo")} htmlFor="ship-seino" required>
              <Input
                id="ship-seino"
                value={shipForm.seinoNo}
                onChange={(event) => setShipForm({ ...shipForm, seinoNo: event.target.value })}
              />
            </Field>
            <Field label={t("shipments.boxCount")} htmlFor="ship-box" required>
              <Input
                id="ship-box"
                inputMode="numeric"
                value={shipForm.boxCount}
                onChange={(event) => setShipForm({ ...shipForm, boxCount: event.target.value })}
              />
            </Field>
          </FormGrid>
          <Button
            variant="primary"
            loading={busy}
            disabled={!shipForm.seinoNo || !/^\d+$/.test(shipForm.boxCount)}
            onClick={() =>
              void run(() =>
                api.post(`/api/shipments/${shipmentId}/ship`, {
                  shippedOn: shipForm.shippedOn,
                  seinoNo: shipForm.seinoNo,
                  boxCount: Number(shipForm.boxCount),
                }),
              )
            }
          >
            {t("shipments.ship")}
          </Button>
        </Card>
      ) : null}

      {canShip && shipment.status === "shipped" ? (
        <Card tone="accent">
          <CardHeader title={t("shipments.osaka")} icon="ship" />
          <FormGrid columns={2}>
            <Field label={t("shipments.osakaArrivedOn")} htmlFor="osaka-on" required>
              <Input
                id="osaka-on"
                type="date"
                value={osakaForm.osakaArrivedOn}
                onChange={(event) =>
                  setOsakaForm({ ...osakaForm, osakaArrivedOn: event.target.value })
                }
              />
            </Field>
            <Field label={t("shipments.vesselOn")} htmlFor="vessel-on">
              <Input
                id="vessel-on"
                type="date"
                value={osakaForm.vesselOn}
                onChange={(event) => setOsakaForm({ ...osakaForm, vesselOn: event.target.value })}
              />
            </Field>
          </FormGrid>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void run(() =>
                api.post(`/api/shipments/${shipmentId}/osaka`, {
                  osakaArrivedOn: osakaForm.osakaArrivedOn,
                  vesselOn: osakaForm.vesselOn || null,
                }),
              )
            }
          >
            {t("shipments.osaka")}
          </Button>
        </Card>
      ) : null}

      {canShip && shipment.status === "osaka_arrived" ? (
        <Card tone="accent">
          <CardHeader title={t("shipments.departJp")} icon="ship" />
          <p className="muted">{t("shipments.departJpHint")}</p>
          <FormGrid columns={2}>
            <Field label={t("shipments.panstarTracking")} htmlFor="panstar" required>
              <Input
                id="panstar"
                value={departForm.panstarTracking}
                onChange={(event) =>
                  setDepartForm({ ...departForm, panstarTracking: event.target.value })
                }
              />
            </Field>
            <Field label={t("shipments.vesselOn")} htmlFor="depart-vessel">
              <Input
                id="depart-vessel"
                type="date"
                value={departForm.vesselOn}
                onChange={(event) =>
                  setDepartForm({ ...departForm, vesselOn: event.target.value })
                }
              />
            </Field>
          </FormGrid>
          <Button
            variant="primary"
            loading={busy}
            disabled={!departForm.panstarTracking}
            onClick={() =>
              void run(() =>
                api.post(`/api/shipments/${shipmentId}/depart-jp`, {
                  panstarTracking: departForm.panstarTracking,
                  vesselOn: departForm.vesselOn || null,
                }),
              )
            }
          >
            {t("shipments.departJp")}
          </Button>
        </Card>
      ) : null}

      {shipment.status === "sailed" ? (
        <Card tone="accent">
          <CardHeader title={t("shipments.arriveKr")} icon="check" />
          <p className="muted">
            {canCloseKr ? t("shipments.arriveKrHint") : t("shipments.arriveKrWaiting")}
          </p>
          {canCloseKr ? (
            <>
              <FormGrid columns={2}>
                <Field label={t("shipments.krArrivedOn")} htmlFor="kr-arrived" required>
                  <Input
                    id="kr-arrived"
                    type="date"
                    value={arriveForm.krArrivedOn}
                    onChange={(event) => setArriveForm({ krArrivedOn: event.target.value })}
                  />
                </Field>
              </FormGrid>
              <Button
                variant="primary"
                loading={busy}
                onClick={() =>
                  void run(() =>
                    api.post(`/api/shipments/${shipmentId}/arrive-kr`, {
                      krArrivedOn: arriveForm.krArrivedOn,
                    }),
                  )
                }
              >
                {t("shipments.arriveKr")}
              </Button>
            </>
          ) : null}
        </Card>
      ) : null}

      <Card padded={false}>
        <CardHeader title={t("shipments.cases")} icon="list" />
        <DataTable
          columns={columns}
          rows={cases}
          rowKey={(row) => row.id}
          onRowClick={(row) => detail.open(row.id)}
          empty={<EmptyState title={t("cases.empty")} />}
        />
      </Card>

      {detail.panel}
    </>
  )
}
